import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AppToaster } from "@/components/shared/AppToaster";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ClientProvider } from "@/contexts/ClientContext";
import { AdminRoute, PortalRoute, PortalRouteWithPasswordCheck } from "@/components/shared/ProtectedRoutes";
import { MainLayout } from "@/components/layout/MainLayout";
import LoginPage from "@/pages/admin/LoginPage";
import EntryPage from "@/pages/EntryPage";
import Dashboard from "@/pages/admin/dashboard/ConfigurableDashboard";
import ClientsLegacyRedirect from "@/pages/admin/ClientsLegacyRedirect";
import TaxReportsModule from "@/pages/admin/TaxReportsModule";
import TasksModule from "@/pages/admin/TasksModule";
import TaxLinesModule from "@/pages/admin/TaxLinesModule";
import NotificationsModule from "@/pages/admin/NotificationsModule";
import SettingsPage from "@/pages/admin/SettingsPage";
import CommunicationsHubPage from "@/pages/admin/CommunicationsHubPage";
import TestLauncher from "@/pages/admin/TestLauncher";
import PortalAdminModule from "@/pages/admin/PortalAdminModule";
import AppointmentsModule from "@/pages/admin/AppointmentsModule";
import QuestionsModule from "@/pages/admin/QuestionsModule";
import SecurityDashboard from "@/pages/admin/SecurityDashboard";
import AuditTrailPage from "@/pages/admin/AuditTrailPage";
import CommunicationsModule from "@/pages/admin/CommunicationsModule";
import TaxRatesPage from "@/pages/admin/TaxRatesPage";
import DocumentsLauncher from "@/pages/admin/DocumentsLauncher";
import InboxPage from "@/pages/admin/InboxPage";
import SmsInboxPage from "@/pages/admin/SmsInboxPage";
import PortalClientsPage from "@/pages/admin/PortalClientsPage";
import BadgesDiagnosticPage from "@/pages/admin/settings/BadgesDiagnosticPage";
import PortalLoginPage from "@/pages/portal/PortalLoginPage";
import PortalForgotPasswordPage from "@/pages/portal/PortalForgotPasswordPage";
import PortalResetPasswordPage from "@/pages/portal/PortalResetPasswordPage";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import ImpersonationEntry from "@/pages/portal/ImpersonationEntry";
import PortalForcePasswordChange from "@/pages/portal/PortalForcePasswordChange";
import PortalPaymentSuccess from "@/pages/portal/PortalPaymentSuccess";
import ContactPublic from "@/pages/public/ContactPublic";
import LegalPage from "@/pages/public/LegalPage";
import PublicBookingPage from "@/pages/public/PublicBookingPage";
import PaymentSuccess from "@/pages/payment/PaymentSuccess";
import InstallPrompt from "@/components/shared/InstallPrompt";
import PaymentCancel from "@/pages/payment/PaymentCancel";
import StripeCheckoutResult from "@/pages/public/StripeCheckoutResult";
import LeaseSignaturePage from "@/pages/public/LeaseSignaturePage";
import LeaseAccessPage from "@/pages/public/LeaseAccessPage";
import RentIncreaseSignPage from "@/pages/public/RentIncreaseSignPage";
import FiscalMandatePage from "@/pages/public/FiscalMandatePage";
import TaxReportSignPage from "@/pages/public/TaxReportSignPage";
import ServiceContractSignPage from "@/pages/public/ServiceContractSignPage";
import QuestionnaireFillPage from "@/pages/public/QuestionnaireFillPage";
import PublicBailSignPage from "@/pages/PublicBailSignPage";
import PublicBailVerifyPage from "@/pages/PublicBailVerifyPage";
import BailWorkflowPage from "@/pages/BailWorkflowPage";
import PortalImmeubleActionPage from "@/pages/portal/immeuble/PortalImmeubleActionPage";
import LeadsPage from "@/pages/admin/LeadsPage";
import AdminImmeubleHub from "@/pages/admin/AdminImmeubleHub";
import AdminImmeubleSubPage from "@/pages/admin/AdminImmeubleSubPage";
import EntrepreneursHub from "@/pages/admin/EntrepreneursHub";
import EntrepreneurSubPage from "@/pages/admin/entrepreneurs/EntrepreneurSubPage";
import PaieHub from "@/pages/admin/PaieHub";
import PaieSubPage from "@/pages/admin/paie/PaieSubPage";
import CabinetHub from "@/pages/admin/CabinetHub";
import HelpHub from "@/pages/admin/HelpHub";
import HelpGuidePage from "@/pages/admin/HelpGuidePage";
import FiscalHub from "@/pages/admin/FiscalHub";
import HomeOfficeCalculatorPage from "@/pages/admin/HomeOfficeCalculatorPage";
import CabinetTaxesPage from "@/pages/admin/CabinetTaxesPage";
import InstalmentsPage from "@/pages/admin/InstalmentsPage";
import CompliancePage from "@/pages/admin/CompliancePage";
import ProfitabilityPage from "@/pages/admin/ProfitabilityPage";
import AcctSubPage from "@/pages/admin/books/AcctSubPage";
import { ComptabiliteRedirect, TransactionsRedirect } from "@/pages/admin/books/AcctRedirects";
import BooksHub from "@/pages/admin/books/BooksHub";
import TimerPage from "@/pages/admin/TimerPage";
import AdminPmsQueuePage from "@/pages/admin/AdminPmsQueuePage";
import TenantReportPage from "@/pages/admin/pms/tenant-report/TenantReportPage";
import CommunicationsLogPage from "@/pages/admin/CommunicationsLogPage";
import { PdfTemplateEditor } from "@/pages/admin/pdfTemplates/PdfTemplateEditor";
import MasterClientsList from "@/pages/admin/MasterClientsList";
import MasterClientDetail from "@/pages/admin/MasterClientDetail";
import Bureau from "@/pages/admin/Bureau";
import T777Submissions from "@/pages/admin/T777Submissions";
import ClientDocumentsPage from "@/pages/admin/ClientDocumentsPage";
import TrashPage from "@/pages/admin/TrashPage";
import ArchiveBinPage from "@/pages/admin/ArchiveBinPage";
// Étape 4 — Entité Fiscal indépendante
import FiscalDossiersList from "@/pages/admin/FiscalDossiersList";
import FiscalDossierDetail from "@/pages/admin/FiscalDossierDetail";
// Étape 2 (Immeuble) — Entité Immeuble DÉCLARATIVE indépendante
import ImmeubleDossiersList from "@/pages/admin/ImmeubleDossiersList";
import ImmeubleDossierDetail from "@/pages/admin/ImmeubleDossierDetail";
import "@/App.css";

function NeutralBoot() {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50" data-testid="neutral-boot">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-3 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
        <p className="text-xs text-gray-400">Chargement...</p>
      </div>
    </div>
  );
}

function KeyboardShortcuts() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "F1") { e.preventDefault(); navigate("/admin"); }
      if (e.ctrlKey && e.key === "n") { e.preventDefault(); navigate("/admin/masters"); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);
  return null;
}

/**
 * Admin login page gate:
 *  - loading  → neutral loader (no admin layout)
 *  - admin logged in → /admin
 *  - else → render login (no cross-role redirect)
 */
function AdminLoginRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <NeutralBoot />;
  if (user) return <Navigate to="/admin" replace />;
  return <LoginPage />;
}

/**
 * Portal login page gate:
 *  - loading → neutral loader (never admin)
 *  - portal logged in → /portal/dashboard
 *  - else → render portal login (never redirect to /admin)
 */
function PortalLoginRedirect() {
  const { portalUser, loading } = useAuth();
  if (loading) return <NeutralBoot />;
  if (portalUser) return <Navigate to="/portal/dashboard" replace />;
  return <PortalLoginPage />;
}

function AdminApp() {
  return (
    <ClientProvider>
      <MainLayout>
        <KeyboardShortcuts />
        <Routes>
          <Route index element={<Dashboard />} />
          {/* Batch #14.285 — Opérations / Satisfaction / Check-listes (templates + instances) retirés → redirections */}
          <Route path="dashboard" element={<Navigate to="/admin/cabinet" replace />} />
          {/* Batch #14.122 — Anciens launchers Finance/IA/Rapport supprimés.
              Routes redirigées pour rétrocompat (breadcrumb + bookmarks). */}
          <Route path="finance" element={<Navigate to="/admin/cabinet" replace />} />
          <Route path="ia" element={<Navigate to="/admin/fiscal" replace />} />
          <Route path="rapport" element={<Navigate to="/admin/fiscal" replace />} />
          <Route path="tax-rates" element={<TaxRatesPage />} />
          {/* Batch #14.143 — Nouveau workflow bail TAL (admin scope) */}
          <Route path="bail-workflow" element={<BailWorkflowPage scope="admin" />} />
          {/* Batch #14.163 — HOTFIX Stripe success/cancel : monte BailWorkflowPage qui détectera session_id */}
          <Route path="bail-workflow/payment-success" element={<BailWorkflowPage scope="admin" />} />
          <Route path="bail-workflow/payment-cancelled" element={<BailWorkflowPage scope="admin" />} />
          <Route path="documents" element={<DocumentsLauncher />} />
          {/* Batch #14.176 — Inbox réception IMAP DGFiscalFlow@dgcomptabilite.ca */}
          <Route path="inbox" element={<InboxPage />} />
          {/* Batch #14.229 — Boîte SMS Twilio (envoyés + reçus) */}
          <Route path="sms-inbox" element={<SmsInboxPage />} />
          {/* Batch #14.260 — Portail clients (fusion Comptes + Gestion des accès) → tuile Cabinet */}
          <Route path="portail-clients" element={<PortalClientsPage />} />
          <Route path="portail/*" element={<Navigate to="/admin/portail-clients" replace />} />
          {/* Batch #14.260 — Diagnostic des pastilles → tuile Paramètres */}
          <Route path="settings/diagnostic-pastilles" element={<BadgesDiagnosticPage />} />
          {/* Batch #14.64 — /admin/clients (TaxClientsModule legacy) retiré.
              Redirect intelligent : ?open=X → /admin/masters?open=X (ouvre fiche),
              sinon → /admin/clients-hub (4 tuiles Master/Fiscal/Immeubles/Autonome). */}
          <Route path="clients" element={<ClientsLegacyRedirect />} />
          <Route path="clients-hub" element={<Navigate to="/admin/masters" replace />} />
          <Route path="clients-trash" element={<TrashPage />} />
          <Route path="archive-bin" element={<ArchiveBinPage />} />
          {/* Batch #14.71 — /admin/parametres/admin-account supprimé.
              Le bouton dans le header redirige vers /admin/settings?tab=account (CompteSection
              propose les mêmes fonctionnalités + 2FA + backup codes). */}
          <Route path="parametres/admin-account" element={<Navigate to="/admin/settings?tab=account" replace />} />
          <Route path="bureau" element={<Bureau />} />
          <Route path="t777-submissions" element={<T777Submissions />} />
          <Route path="client-documents" element={<ClientDocumentsPage />} />
          {/* Batch #14.67 — /admin/clients/new (ClientCreation) supprimé.
              Redirige vers /admin/masters (la nouvelle création passe par "Nouveau dossier Master"). */}
          <Route path="clients/new" element={<Navigate to="/admin/masters" replace />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="checklist-templates" element={<Navigate to="/admin/tasks" replace />} />
          <Route path="satisfaction" element={<Navigate to="/admin/cabinet" replace />} />
          {/* Batch #14.114 — Hubs sidebar : Cabinet + Fiscal (style poupée russe avec tuiles) */}
          <Route path="cabinet" element={<CabinetHub />} />
          {/* Batch #14.302 — Cabinet › Mode d'emploi et aide (guides fixes, PDF) */}
          <Route path="aide" element={<HelpHub />} />
          <Route path="aide/:guideId" element={<HelpGuidePage />} />
          <Route path="fiscal" element={<FiscalHub />} />
          {/* Batch #14.243 — Hub Entrepreneurs (ex-« Autonome ») + sous-pages par dossier */}
          <Route path="entrepreneurs" element={<EntrepreneursHub />} />
          <Route path="entrepreneurs/:masterId/:tileId" element={<EntrepreneurSubPage />} />
          <Route path="autonome" element={<Navigate to="/admin/entrepreneurs" replace />} />
          {/* Batch #14.250 — Hub Paie + sous-pages par dossier (itemId optionnel = période de paie) */}
          <Route path="paie" element={<PaieHub />} />
          <Route path="paie/:masterId/:tileId/:itemId?" element={<PaieSubPage />} />
          {/* Batch #14.116 — Calculatrice bureau à domicile (T777) — page dédiée */}
          <Route path="home-office-calculator" element={<HomeOfficeCalculatorPage />} />
          <Route path="cabinet-taxes" element={<CabinetTaxesPage />} />
          <Route path="acomptes" element={<InstalmentsPage />} />
          <Route path="conformite" element={<CompliancePage />} />
          <Route path="rentabilite" element={<ProfitabilityPage />} />
          {/* Batch #14.274 — Comptabilité du cabinet : mêmes tuiles / écrans que la Tenue de livres (AcctSubPage, API /gl).
              Anciennes URL (?tab=, transactions-…/sub, invoices, expenses, products) redirigées. */}
          <Route path="comptabilite" element={<ComptabiliteRedirect />} />
          <Route path="comptabilite/transactions-clients/:sub" element={<TransactionsRedirect kind="clients" />} />
          <Route path="comptabilite/transactions-fournisseurs/:sub" element={<TransactionsRedirect kind="fournisseurs" />} />
          <Route path="comptabilite/:tileId" element={<AcctSubPage scope="cabinet" />} />
          <Route path="invoices" element={<Navigate to="/admin/comptabilite/transactions-clients" replace />} />
          <Route path="expenses" element={<Navigate to="/admin/comptabilite/transactions-fournisseurs" replace />} />
          <Route path="products" element={<Navigate to="/admin/comptabilite/produits" replace />} />
          {/* Batch #14.271 — Tenue de livres par dossier client (mêmes tuiles, API /books/{master_id}) */}
          <Route path="tenue-livres" element={<BooksHub />} />
          <Route path="tenue-livres/:masterId/:tileId" element={<AcctSubPage scope="books" />} />
          {/* Batch #14.235 — Timer sessions de travail à taux horaire */}
          <Route path="timer" element={<TimerPage />} />
          <Route path="checklists" element={<Navigate to="/admin/tasks" replace />} />
          {/* Batch #14.103 — Hub Immeubles 7 tuiles + sous-pages nested.
              ⚠️ Garder cet ordre : la route nested `:masterId/:tileId` doit venir
              avant les autres routes commençant par `immeubles-`. */}
          <Route path="immeubles" element={<AdminImmeubleHub />} />
          <Route path="immeubles/:masterId/:tileId" element={<AdminImmeubleSubPage />} />
          <Route path="pms-queue" element={<AdminPmsQueuePage />} />
          <Route path="pms-queue/enquete/:checkId/rapport" element={<TenantReportPage />} />
          <Route path="communications" element={<CommunicationsLogPage />} />
          <Route path="pdf-templates" element={<Navigate to="/admin/client-documents?tab=modeles" replace />} />
          <Route path="pdf-templates/:id" element={<PdfTemplateEditor />} />
          {/* Legacy redirects (rétrocompat bookmarks) — toutes les anciennes URLs immeuble pointent vers le Hub */}
          <Route path="immeubles-legacy" element={<Navigate to="/admin/immeubles" replace />} />
          <Route path="immeubles-legacy/*" element={<Navigate to="/admin/immeubles" replace />} />
          <Route path="base-immeuble" element={<Navigate to="/admin/immeubles" replace />} />
          <Route path="base-immeuble/:id" element={<MasterClientDetail />} />
          <Route path="masters" element={<MasterClientsList />} />
          <Route path="masters/:id" element={<MasterClientDetail />} />
          <Route path="base-fiscale" element={<Navigate to="/admin/fiscal-dossiers" replace />} />
          <Route path="base-fiscale/:id" element={<MasterClientDetail />} />
          {/* Étape 4 — Entité Fiscal indépendante (vraie séparation visuelle) */}
          <Route path="fiscal-dossiers" element={<FiscalDossiersList />} />
          <Route path="fiscal-dossiers/:id" element={<FiscalDossierDetail />} />
          <Route path="immeuble-dossiers" element={<ImmeubleDossiersList />} />
          <Route path="immeuble-dossiers/:id" element={<ImmeubleDossierDetail />} />
          {/* Iter75 — Documents launcher sub-routes (isolated views) */}
          <Route path="documents/sent" element={<CommunicationsModule mode="sent" />} />
          <Route path="documents/received" element={<CommunicationsModule mode="received" />} />
          {/* Batch #14.90 — /documents/signatures supprimé. Le seul flow de signature
              admin-side est désormais "Documents à signer" via /admin/client-documents. */}
          {/* Batch #14.157 — /admin/messages supprimé : tuile « Chat clients » du
              DocumentsLauncher pointe directement sur /admin/questions (plus de tuile-dans-tuile). */}
          <Route path="messages" element={<Navigate to="/admin/questions" replace />} />
          <Route path="appointments" element={<AppointmentsModule />} />
          <Route path="questions" element={<QuestionsModule />} />
          <Route path="tasks" element={<TasksModule />} />
          <Route path="tax-lines" element={<TaxLinesModule />} />
          <Route path="reports" element={<TaxReportsModule />} />
          <Route path="notifications" element={<NotificationsModule />} />
          <Route path="security" element={<SecurityDashboard />} />
          <Route path="audit" element={<AuditTrailPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="communications-hub" element={<CommunicationsHubPage />} />
          <Route path="test" element={<TestLauncher />} />
          <Route path="portal-admin" element={<PortalAdminModule />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </MainLayout>
    </ClientProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <InstallPrompt />
          <Routes>
          {/* Entry */}
          <Route path="/" element={<EntryPage />} />

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLoginRedirect />} />
          <Route path="/login" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/*" element={<AdminRoute><AdminApp /></AdminRoute>} />

          {/* Portal */}
          <Route path="/portal" element={<PortalLoginRedirect />} />
          <Route path="/portal/as/:key" element={<ImpersonationEntry />} />
          <Route path="/portal/forgot-password" element={<PortalForgotPasswordPage />} />
          <Route path="/portal/reset-password" element={<PortalResetPasswordPage />} />
          <Route path="/portal/change-password" element={<PortalRoute><PortalForcePasswordChange /></PortalRoute>} />
          <Route path="/portal/dashboard" element={<PortalRouteWithPasswordCheck><PortalDashboard /></PortalRouteWithPasswordCheck>} />
          {/* Batch #14.143 — Nouveau workflow bail TAL (portail propriétaire) */}
          <Route path="/portal/bail-workflow" element={<PortalRouteWithPasswordCheck><BailWorkflowPage scope="portal" /></PortalRouteWithPasswordCheck>} />
          {/* Batch #14.163 — HOTFIX Stripe success/cancel portail */}
          <Route path="/portal/bail-workflow/payment-success" element={<PortalRouteWithPasswordCheck><BailWorkflowPage scope="portal" /></PortalRouteWithPasswordCheck>} />
          <Route path="/portal/bail-workflow/payment-cancelled" element={<PortalRouteWithPasswordCheck><BailWorkflowPage scope="portal" /></PortalRouteWithPasswordCheck>} />
          {/* Batch #14.150 — Tuiles actions immeuble en plein écran */}
          <Route path="/portal/immeuble/action/:actionId" element={<PortalRouteWithPasswordCheck><PortalImmeubleActionPage /></PortalRouteWithPasswordCheck>} />

          {/* Iter81 — Page publique de contact / RDV (no auth) */}
          <Route path="/contact-public" element={<ContactPublic />} />
          {/* Batch #14.309 — pages légales publiques (exigées par Intuit pour QuickBooks) */}
          <Route path="/conditions-utilisation" element={<LegalPage slug="conditions-utilisation" />} />
          <Route path="/politique-confidentialite" element={<LegalPage slug="politique-confidentialite" />} />
          {/* Iter100 — Page publique de réservation Calendly-like */}
          <Route path="/book" element={<PublicBookingPage />} />

          {/* PSI Phase 2 — Stripe Checkout return pages */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          {/* Batch #14.26 — Page de succès dédiée aux paiements portail client
              (facture + enquête de crédit). Beau visuel + bouton "Retour au portail". */}
          <Route path="/portal/payment-success" element={<PortalPaymentSuccess />} />

          {/* Étape 4 — Stripe Checkout RESULT (loyers locataires, page publique) */}
          <Route path="/stripe/checkout/result" element={<StripeCheckoutResult />} />
          <Route path="/sign/:token" element={<LeaseSignaturePage />} />
          <Route path="/sign/rent-increase/:token" element={<RentIncreaseSignPage />} />
          {/* Batch #14.236 — Mandat fiscal public (magic link, aucune connexion) */}
          <Route path="/mandat-fiscal/:token" element={<FiscalMandatePage />} />
          {/* Batch #14.239 — Rapport d'impôt public à signer (magic link) */}
          <Route path="/rapport-impot/:token" element={<TaxReportSignPage />} />
          {/* Batch #14.240 — Contrat de services public à signer (magic link) */}
          <Route path="/contrat-service/:token" element={<ServiceContractSignPage />} />
          {/* Batch #14.242 — Questionnaire public à remplir + signer (magic link) */}
          <Route path="/questionnaire/:token" element={<QuestionnaireFillPage />} />
          <Route path="/sign/bail/:token" element={<PublicBailSignPage />} />
          {/* Batch #14.162 — Page publique de vérification d'un bail signé (QR code) */}
          <Route path="/verify/bail/:workflowId" element={<PublicBailVerifyPage />} />
          <Route path="/access/:token" element={<LeaseAccessPage />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <AppToaster />
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
